<script setup lang="ts">
import {ref, watch, nextTick} from "vue";
import {useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {
    deleteUser,
    getUser as apiGetUser,
    remindUserVerifyEmail,
    verifyUserEmail,
    updateUserPlan as apiUpdateUserPlan,
    impersonateUser,
} from "@/Services/Admin/admin-users";
import {getPlans} from "@/Services/Admin/admin-plans";
import {getting} from "@/Composables/api";
import Str from "@/Utils/Str";
import {Avatar, Button, Card, Chip, Menu, Select} from "@/Components/Library";
import {PageHeading, PageLoading} from "@/Components/Global";
import GroupsBlock from "@/Pages/Admin/Groups/GroupsBlock.vue";
import ContactsBlock from "../Contacts/ContactsBlock.vue";
import {useMainStore} from "@/Stores/store";
import dayjs from "dayjs";

const $store = useMainStore();
const confirm = useConfirm();
const router = useRouter();

const userMenuContainer = ref<Menu>();
const userMenuItems = [
    {
        label: "Edit",
        icon: "prime:pencil",
        command: () => {
            router.push({
                name: "admin.users.edit",
                params: {user: router.currentRoute.value.params.user},
            });
        },
    },
    {
        label: "Archive",
        icon: "prime:trash",
        class: "text-red-600 hover:bg-red-100",
        command: () => archiveUser(),
    },
];
const user = ref();
const plans = ref([]);
getPlans().then(({data}) => (plans.value = data));
const getUser = async () =>
    apiGetUser(router.currentRoute.value.params.user, router.currentRoute.value.query).then(({data}) => {
        console.log(data);
        user.value = data;
        plan.value = data.plan_id;
    });

getUser();

const archiveUser = () => {
    confirm.require({
        message: "Are you sure you want to archive this user?",
        accept: async () => {
            await deleteUser(router.currentRoute.value.params.user);
            $store.toastMessages.push({
                severity: "success",
                summary: "User archived",
                detail: "The user has been archived successfully.",
                life: 3000,
            });
            setTimeout(() => {
                router.push({name: "admin.users.index"});
            }, 3000);
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};

const remindToVerifyEmail = async () => {
    await remindUserVerifyEmail(router.currentRoute.value.params.user);
    $store.toastMessages.push({
        severity: "success",
        summary: "Email verification reminder sent",
        detail: "The user has been reminded to verify their email.",
        life: 3000,
    });
};

const verifyEmail = () => {
    confirm.require({
        message: "Are you sure you want to verify this user's email?",
        accept: async () => {
            await verifyUserEmail(router.currentRoute.value.params.user);
            $store.toastMessages.push({
                severity: "success",
                summary: "Email verified",
                detail: "The user's email has been verified successfully.",
                life: 3000,
            });
            await getUser();
        },
        acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};

const plan = ref(null);

const updateUserPlan = async (planId: string) => {
    confirm.require({
        header: "Change User Plan",
        message: "Are you sure you want to change this user's plan?",
        accept: async () => {
            const {data} = await apiUpdateUserPlan(user.value.id, planId);
            user.value = data;
            $store.toastMessages.push({
                severity: "success",
                summary: "Plan updated",
                detail: "The user's plan has been updated successfully.",
                life: 3000,
            });
        },
        reject: async () => {
            plan.value = user.value.plan_id;
            await nextTick();
            confirm.close();
        },
        acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
        group: "dialog",
    });
};

const impersonate = async () => {
    await impersonateUser(user.value.id);
    window.location.href = "/";
};

watch(plan, (newPlan, oldPlan) => {
    if (oldPlan && newPlan !== oldPlan) {
        updateUserPlan(newPlan);
    }
});
</script>
<template>
    <div class="mx-auto w-full max-w-7xl pb-12">
        <div class="m-container mb-3">
            <PageHeading title="User">
                <Button
                    :icon="{
                        icon: 'pepicons-pencil:dots-y',
                        width: '24',
                    }"
                    class="ml-auto !ring-0"
                    @click.stop="userMenuContainer.menu.toggle($event)"
                />
                <Menu ref="userMenuContainer" popup :model="userMenuItems" />
            </PageHeading>
        </div>
        <PageLoading v-if="getting.includes('user') && !getting.includes('groups') && !getting.includes('contacts')" />
        <div v-if="user" class="flex flex-col gap-4">
            <div class="flex flex-col gap-4 md:flex-row">
                <Card pt:body:class="h-full" pt:content:class="h-full flex items-center justify-center">
                    <div class="my-auto flex flex-col items-center justify-center p-4">
                        <Avatar
                            :user="user"
                            class="mb-4 aspect-square w-full max-w-36 text-4xl"
                            :class="[{'p-8': !user.profile?.avatar?.url}]"
                            shape="circle"
                        />
                        <div class="mb-4 flex flex-col items-center gap-2">
                            <h2 class="text-2xl font-bold">{{ user.first_name }} {{ user.last_name }}</h2>
                            <a :href="'mailto:' + user.email" class="cursor-pointer hover:underline">
                                {{ user.email }}
                            </a>
                        </div>
                        <div class="mb-4 flex flex-wrap gap-2">
                            <template v-for="(role, i) in user.roles" :key="i">
                                <Chip :label="Str.of(role).title().value" />
                            </template>
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <Button
                                class="bg-black font-medium text-white"
                                :icon="{
                                    icon: 'mdi:spy',
                                    width: 16,
                                }"
                                @click="impersonate"
                            >
                                Impersonate
                            </Button>
                        </div>
                    </div>
                </Card>
                <Card>
                    <template #title>Details</template>
                    <template #content>
                        <table v-if="user">
                            <tbody>
                                <tr>
                                    <td>User ID</td>
                                    <td>{{ user.id }}</td>
                                </tr>
                                <tr>
                                    <td>Country</td>
                                    <td>{{ user.profile?.country ?? "Not set" }}</td>
                                </tr>
                                <tr>
                                    <td>Plan</td>
                                    <td>
                                        <Select
                                            v-model="plan"
                                            :options="plans"
                                            option-label="name"
                                            option-value="id"
                                            placeholder="Select a plan"
                                            class="w-full"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>Email Verified At</td>
                                    <td>
                                        <span v-if="user.email_verified_at">
                                            {{ dayjs(user.email_verified_at).format("DD/MM/YYYY HH:mm") }}
                                        </span>
                                        <div v-else class="flex items-center gap-4">
                                            <span>Not verified</span>
                                            <div class="flex gap-2">
                                                <Button
                                                    class="bg-askr-green !p-2 text-white hover:brightness-110"
                                                    :icon="{
                                                        icon: 'mdi:check',
                                                        width: 24,
                                                    }"
                                                    @click="verifyEmail"
                                                />
                                                <Button
                                                    class="bg-blue-500 !p-2 text-white hover:brightness-110"
                                                    :icon="{
                                                        icon: 'mdi:email',
                                                        width: 24,
                                                    }"
                                                    @click="remindToVerifyEmail"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Created At</td>
                                    <td>
                                        {{ dayjs(user.created_at).format("DD/MM/YYYY HH:mm") }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Updated At</td>
                                    <td>
                                        {{ dayjs(user.updated_at).format("DD/MM/YYYY HH:mm") }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Archived At</td>
                                    <td>
                                        {{
                                            user.deleted_at
                                                ? dayjs(user.deleted_at).format("DD/MM/YYYY HH:mm")
                                                : "Not archived"
                                        }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </Card>
            </div>
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <GroupsBlock :user="user" />
                <ContactsBlock :user="user" />
            </div>
        </div>
    </div>
</template>
<style scoped>
td {
    @apply py-1.5 pr-8;
}

tr > td:first-child {
    @apply font-bold;
}
</style>
